<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref } from 'vue'
import type { ConversationInfo } from '../api/types'
import type { StoredMessage } from '../storage/db'
import { describeError } from '../api/http'
import { useSession } from '../stores/session'
import { useComposer } from '../stores/composer'
import { mentionable, mentionQuery, mentionedIn, suggest, type Person } from '../lib/mentions'
import { watch } from 'vue'

const props = defineProps<{
  conv: ConversationInfo
  placeholder: string
  /** A thread's box: what it sends replies to the thread's top-level message. */
  thread?: StoredMessage
}>()
/** Test ids: the thread's box has its own, beside the conversation's. */
const tid = (name: string) => (props.thread ? `thread-${name}` : name)
const session = useSession()
const text = ref('')
const sending = ref(0)
// Suspended in this space (HRTH-95): the server refuses my messages until it ends.
const now = ref(Date.now())
const tick = setInterval(() => (now.value = Date.now()), 15_000)
onBeforeUnmount(() => clearInterval(tick))
const suspendedUntil = computed(() => {
  const space = session.spaces.find((s) => s.id === props.conv.space_id)
  const until = space?.members.find((m) => m.profile.user_id === session.me)?.suspended_until ?? 0
  return until > now.value ? until : null
})
const ready = computed(() => (session.engine?.hasGroup(props.conv.id) ?? false) && !suspendedUntil.value)

const MAX_LEN = 4000
const composer = useComposer()
const mode = computed(() =>
  composer.mode?.conv === props.conv.id && composer.mode.thread === props.thread?.envelope.id ? composer.mode : null,
)
const box = ref<HTMLTextAreaElement | null>(null)
watch(mode, (m) => {
  if (m) text.value = m.draft ?? ''
  if (m) box.value?.focus()
})
// Grow the box with its text (up to max-h-48, then it scrolls): a one-line box hides most
// of a long message, worst on phones. iOS Safari has no field-sizing, so measure it.
function fit(): void {
  const el = box.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = `${el.scrollHeight}px`
}
watch(text, () => void nextTick(fit))
// Rotating a phone rewraps the text.
window.addEventListener('resize', fit)
onBeforeUnmount(() => window.removeEventListener('resize', fit))
// @mentions: typing `@` and the start of a name lists the people here to pick from.
const caret = ref(0)
const picked = ref(0)
const dismissed = ref<number | null>(null)
const people = computed(() => mentionable(session.profiles, session.spaces, props.conv))
const query = computed(() => mentionQuery(text.value, caret.value))
const suggestions = computed<Person[]>(() => {
  const q = query.value
  return q && q.start !== dismissed.value ? suggest(people.value, q.query, session.me) : []
})
watch(suggestions, () => (picked.value = 0))
function trackCaret(): void {
  caret.value = box.value?.selectionStart ?? text.value.length
}
function pick(p: Person): void {
  const q = query.value
  if (!q) return
  const insert = `@${p.name} `
  const end = q.start + 1 + q.query.length
  text.value = text.value.slice(0, q.start) + insert + text.value.slice(end)
  const at = q.start + insert.length
  caret.value = at
  void nextTick(() => {
    box.value?.focus()
    box.value?.setSelectionRange(at, at)
  })
}

const fileInput = ref<HTMLInputElement | null>(null)
// Files go to the conversation: a media message can't be a reply, so threads take text.
const uploads = computed(() => (props.thread ? [] : (session.uploads[props.conv.id] ?? [])))

async function sendFiles(files: FileList | File[] | null | undefined): Promise<void> {
  for (const f of Array.from(files ?? [])) {
    try {
      await session.sendFile(props.conv.id, f)
    } catch (e) {
      session.toast(`Couldn't send ${f.name}: ${describeError(e)}`, 'error')
    }
  }
}

function onPaste(e: ClipboardEvent): void {
  if (props.thread) return
  const files = e.clipboardData?.files
  if (files && files.length) {
    e.preventDefault()
    void sendFiles(files)
  }
}

function onDrop(e: DragEvent): void {
  if (!props.thread && e.dataTransfer?.files.length) void sendFiles(e.dataTransfer.files)
}

/**
 * Sends immediately and clears the box, even if earlier messages are still in flight:
 * the engine queues sends in order, so quick consecutive messages are never dropped.
 */
async function send(): Promise<void> {
  const body = text.value.trim()
  if (!body || !ready.value) return
  const m = mode.value
  text.value = ''
  composer.clear()
  sending.value++
  try {
    if (m) await session.editMessage(props.conv.id, m.target.envelope.id, body)
    else await session.send(props.conv.id, body, props.thread?.envelope.id, mentionedIn(body, people.value, session.me))
  } catch (e) {
    // Give the text back if the box is still empty, so nothing is lost.
    if (!text.value) text.value = body
    session.toast(`Message not sent: ${describeError(e)}`, 'error')
  } finally {
    sending.value--
  }
}

function onKey(e: KeyboardEvent): void {
  const list = suggestions.value
  if (list.length && !e.isComposing) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      picked.value = (picked.value + (e.key === 'ArrowDown' ? 1 : list.length - 1)) % list.length
      return
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      pick(list[picked.value] ?? list[0]!)
      return
    }
    if (e.key === 'Escape') {
      dismissed.value = query.value?.start ?? null
      return
    }
  }
  if (e.key === 'Escape' && mode.value) {
    text.value = ''
    composer.clear()
    return
  }
  if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
    e.preventDefault()
    void send()
  }
}
</script>

<template>
  <form class="shrink-0 px-6 pb-5 max-md:px-4" @submit.prevent="send" @dragover.prevent @drop.prevent="onDrop">
    <div v-for="u in uploads" :key="u.name" class="mono mb-2 border border-border px-3 py-2 text-text-muted" data-testid="upload-progress">
      <div class="flex justify-between">
        <span class="truncate">{{ u.stage === 'encrypting' ? 'ENCRYPT' : 'UPLOAD' }} · {{ u.name }}</span>
        <span>{{ String(Math.round(u.progress * 100)).padStart(3, '0') }}%</span>
      </div>
      <div class="mt-1.5 h-px overflow-hidden bg-border">
        <div class="h-full bg-ink transition-all" :style="{ width: `${u.progress * 100}%` }" />
      </div>
    </div>
    <div v-if="mode" class="mono mb-1.5 flex items-center gap-3 text-text-muted" :data-testid="tid('composer-mode')">
      <span>Editing message</span>
      <button class="text-text-primary underline-offset-2 hover:underline" @click="(composer.clear(), (text = ''))">cancel</button>
      <span class="text-text-disabled">esc</span>
    </div>
    <ul v-if="suggestions.length" class="mb-1.5 border border-border bg-bg-primary py-1" role="listbox" aria-label="Mention someone" data-testid="mention-list">
      <li
        v-for="(p, i) in suggestions"
        :key="p.id"
        role="option"
        :aria-selected="i === picked"
        class="flex cursor-pointer items-baseline gap-2 px-3 py-1.5"
        :class="i === picked ? 'bg-ink text-paper' : 'text-text-primary hover:bg-bg-secondary'"
        data-testid="mention-option"
        @mousedown.prevent="pick(p)"
      >
        <span>@{{ p.name }}</span>
        <span class="mono truncate" :class="i === picked ? 'opacity-70' : 'text-text-muted'">{{ p.id.split('@')[0] }}</span>
      </li>
    </ul>
    <!-- On phones the buttons drop below the text so it gets the full width. -->
    <div class="flex items-end gap-2 border border-border bg-bg-primary px-2 py-1.5 transition-colors max-sm:flex-wrap focus-within:border-ink hover:border-border-strong">
      <button
        v-if="!thread"
        type="button"
        class="mono px-2 py-1.5 text-text-muted hover:text-text-primary disabled:opacity-40"
        title="Attach a file. People download it to their devices; unless this server keeps attachments, it leaves the server once everyone has it, or after 30 days."
        aria-label="Attach a file"
        :disabled="!ready"
        :data-testid="tid('attach')"
        @click="fileInput?.click()"
      >
        +FILE
      </button>
      <input
        v-if="!thread"
        ref="fileInput"
        type="file"
        multiple
        class="hidden"
        :data-testid="tid('file-input')"
        @change="sendFiles(($event.target as HTMLInputElement).files); ($event.target as HTMLInputElement).value = ''"
      />
      <textarea
        ref="box"
        v-model="text"
        rows="1"
        class="max-h-48 min-h-6 flex-1 resize-none max-sm:order-first max-sm:basis-full max-sm:px-2 bg-transparent py-1 text-[14.5px] text-text-primary placeholder:text-text-disabled focus:outline-none focus-visible:outline-none"
        :placeholder="
          suspendedUntil ? `You're suspended here until ${new Date(suspendedUntil).toLocaleString()}` : ready ? placeholder : 'Joining…'
        "
        :disabled="!ready"
        :maxlength="MAX_LEN"
        aria-label="Message"
        :data-testid="tid('composer')"
        @keydown="onKey"
        @keyup="trackCaret"
        @click="trackCaret"
        @input="trackCaret"
        @paste="onPaste"
      />
      <button
        class="btn-primary h-7 max-sm:ml-auto disabled:border-border disabled:bg-transparent disabled:text-text-disabled"
        :disabled="!text.trim() || !ready"
        :data-testid="tid('send')"
      >
        Send
      </button>
    </div>
  </form>
</template>
